/* ----------------------------------------------------------------------------
 * leaseMachine — the PURE decision core of the cross-client single-active-voice-
 * session lease (voice residual #3). No DOM, no network, no timers — every
 * function here is a pure input->output map so the arbitration logic is unit-
 * testable in-process (Node --test), exactly like reconcileActiveDomain. The
 * useVoiceLease hook is a thin wiring shell over these functions; the wake gate
 * consults leaseAllowsArm() only.
 *
 * FAIL-OPEN is the whole safety posture: a single-device user must NEVER lose
 * voice to an arbiter blip. gate 'open' (API unreachable/errored) ARMS as today.
 * -------------------------------------------------------------------------- */

/** Heartbeat cadence while holding — 3 beats fit inside the 45s server TTL. */
export const LEASE_HEARTBEAT_MS = 15_000;
/** Mirror of the server-side VoiceLeaseStore::TTL_SECONDS (informational). */
export const LEASE_TTL_MS = 45_000;
/** Background re-acquire backoff bounds when fail-open (gate 'open'). */
export const LEASE_RETRY_BASE_MS = 2_000;
export const LEASE_RETRY_MAX_MS = 60_000;

/**
 * The lease gate a client is in:
 *   'held'  — this client holds the lease  => it MAY arm hands-free wake.
 *   'other' — another client/device holds it => it must NOT arm; show the pill.
 *   'open'  — fail-open (lease API errored/unreachable) => arm as TODAY (beacon).
 */
export type LeaseGate = 'held' | 'other' | 'open';

/** The wire holder record returned by the server (snake_case matches the API). */
export interface LeaseHolder {
  cid: string;
  label: string;
  since: number;
}

/** An acquire/renew POST envelope (only the fields the client reads). */
export interface LeasePostResponse {
  ok?: boolean;
  op?: string;
  held?: boolean;
  holder?: LeaseHolder | null;
  expires_in?: number;
  ttl?: number;
  released?: boolean;
}

/** The reduced client view derived from a lease outcome. */
export interface LeaseView {
  gate: LeaseGate;
  /** the OTHER holder's short label (for the pill) — null unless gate==='other'. */
  holderLabel: string | null;
}

/** Interpret a successful acquire/renew response into a gate + pill label. */
export function interpretLeaseResponse(res: LeasePostResponse | null | undefined): LeaseView {
  if (res && res.held === true) {
    return { gate: 'held', holderLabel: null };
  }
  const label = res?.holder?.label ?? '';
  return { gate: 'other', holderLabel: label !== '' ? label : null };
}

/** The fail-open view — used when the lease API throws / is unreachable. */
export function failOpenView(): LeaseView {
  return { gate: 'open', holderLabel: null };
}

/**
 * The ONLY question the wake_host_gate asks of the lease: may this client arm?
 * TRUE when it holds the lease OR the arbiter is unreachable (fail-open). FALSE
 * only when another live device demonstrably holds it. PTT never calls this.
 */
export function leaseAllowsArm(gate: LeaseGate): boolean {
  return gate === 'held' || gate === 'open';
}

/**
 * The wake_host_gate mount decision. leaseActive = this client is wake-eligible
 * and its mic is not killed (it WOULD arm today); it actually mounts the wake
 * engine only when it may arm per the lease (holds it, or fail-open). This is
 * the acquire-before-arm gate — a non-holder (canArm false) never mounts.
 */
export function shouldMountWakeEngine(leaseActive: boolean, canArm: boolean): boolean {
  return leaseActive && canArm;
}

/** Show the "listening on another device / Listen here" takeover pill: only for
 *  a wake-eligible client that a DIFFERENT live device currently holds. */
export function shouldShowTakeoverPill(leaseActive: boolean, gate: LeaseGate): boolean {
  return leaseActive && gate === 'other';
}

/** Which lease op the next tick should run: renew while holding, else (re)acquire. */
export type LeaseOp =
  | { kind: 'renew' }
  | { kind: 'acquire'; takeover: boolean };

export function chooseLeaseOp(held: boolean, takeoverPending: boolean): LeaseOp {
  return held ? { kind: 'renew' } : { kind: 'acquire', takeover: takeoverPending };
}

/** Exponential backoff for the fail-open background re-acquire (capped). */
export function nextRetryDelayMs(prevMs: number): number {
  const next = prevMs <= 0 ? LEASE_RETRY_BASE_MS : prevMs * 2;
  return Math.min(next, LEASE_RETRY_MAX_MS);
}

/**
 * Derive a short, human "Browser on OS" label from a User-Agent (+ optional
 * platform hint) — shown to the OTHER device so a takeover names who it displaces.
 * Pure and defensive: an unknown UA degrades to 'this device', never throws.
 * Order matters (Edge before Chrome; iOS/Android before generic).
 */
export function deriveClientLabel(ua: string, platformHint = ''): string {
  const s = (ua || '').toLowerCase();
  const p = (platformHint || '').toLowerCase();

  let os = '';
  if (/iphone|ipad|ipod/.test(s) || /iphone|ipad|ipod/.test(p)) os = 'iOS';
  else if (/android/.test(s)) os = 'Android';
  else if (/windows/.test(s) || /win/.test(p)) os = 'Windows';
  else if (/mac os x|macintosh/.test(s) || /mac/.test(p)) os = 'macOS';
  else if (/cros/.test(s)) os = 'ChromeOS';
  else if (/linux/.test(s) || /linux/.test(p)) os = 'Linux';

  let browser = '';
  if (/edg\//.test(s) || /edge/.test(s)) browser = 'Edge';
  else if (/opr\/|opera/.test(s)) browser = 'Opera';
  else if (/samsungbrowser/.test(s)) browser = 'Samsung Internet';
  else if (/firefox|fxios/.test(s)) browser = 'Firefox';
  else if (/chrome|crios|chromium/.test(s)) browser = 'Chrome';
  else if (/safari/.test(s)) browser = 'Safari';

  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;
  if (os) return os;
  return 'this device';
}
